import { WorkingCompany } from "@/domain/features/company/v2/entities/working_company/working_company"
import { type FirebaseFirestoreInfra } from "@/infrastructure/firebase_firestore/core/firestore"
import { type Unsubscribe } from "firebase/auth"
import {
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  type QueryConstraint,
} from "firebase/firestore"

export class WorkingCompanyRepository {
  constructor(private readonly firestore: FirebaseFirestoreInfra) {}

  private collectionReference(
    params: WorkingCompany.firestore.ParameterOfCollectionPath
  ) {
    return this.firestore.collectionReference<WorkingCompany>(
      WorkingCompany.firestore.collectionPath(params)
    )
  }

  async saveCurrentCompany({
    userId,
    companyId,
  }: {
    userId: string
    companyId: string
  }): Promise<void> {
    const documentReference = this.firestore.documentReference(
      WorkingCompany.firestore.parentDocumentPath({ userId })
    )

    await setDoc(
      documentReference,
      {
        currentCompanyId: companyId,
      },
      { merge: true }
    )
  }

  async fetchOrNot({
    userId,
  }: {
    userId: string
  }): Promise<WorkingCompany | undefined> {
    const ref = query(this.collectionReference({ userId }), limit(1))
    const snapshot = await getDocs(ref)

    return snapshot.docs.at(0)?.data()
  }

  async fetchCurrentCompanyId({
    userId,
  }: {
    userId: string
  }): Promise<string | undefined> {
    const documentReference = this.firestore.documentReference(
      WorkingCompany.firestore.parentDocumentPath({ userId })
    )

    const documentSnapshot = await getDoc(documentReference)

    if (documentSnapshot.exists()) {
      const data = documentSnapshot.data()

      return data.currentCompanyId as string
    }
  }

  // 後々の複数企業所属の拡張性を持たせるため、所属企業はコレクションで持たせている
  // しかし、現状は１企業が前提の設計なので、limit(1) で取得する
  async fetchRequired({ userId }: { userId: string }): Promise<WorkingCompany> {
    const ref = query(this.collectionReference({ userId }), limit(1))
    const snapshot = await getDocs(ref)

    return snapshot.docs.at(0)!.data()
  }

  async fetchList({ userId }: { userId: string }): Promise<WorkingCompany[]> {
    const ref = this.collectionReference({ userId })
    const snapshot = await getDocs(ref)

    return snapshot.docs.map((doc) => doc.data())
  }

  watchList({
    userId,
    onData,
    limit: paramsLimit,
  }: {
    userId: string
    onData: (data: WorkingCompany[]) => void
    limit?: number
  }): Unsubscribe {
    const queryConstraints: QueryConstraint[] = []

    if (paramsLimit != null) {
      queryConstraints.push(limit(paramsLimit))
    }
    const ref = query(this.collectionReference({ userId }), ...queryConstraints)

    return onSnapshot(ref, (snapshot) => {
      onData(snapshot.empty ? [] : snapshot.docs.map((doc) => doc.data()))
    })
  }
}
